import { User } from '@bioliberty/lifemind';
import { useCallback } from 'react';
import { useInviteUserMutation } from '../../api';
import { Button } from '../../components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/Dialog';
import { useToaster } from '../../hooks/useToaster';

interface ResendInviteUserModalProps {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ResendInviteUserModal = ({ open, onOpenChange, user }: ResendInviteUserModalProps) => {
  const toast = useToaster();
  const [inviteUser, { isLoading }] = useInviteUserMutation();

  const onResendClick = useCallback(async () => {
    try {
      await inviteUser({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        roles: user.roles,
        resend: true
      }).unwrap();

      toast.success(`An invitation has been sent to ${user.email}.`);
      onOpenChange(false);
    } catch {
      toast.error('Failed to send invitation email. Please try again.');
    }
  }, [inviteUser, onOpenChange, toast, user.email, user.firstName, user.lastName, user.roles]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>Resend Invitation Email</DialogTitle>
          <DialogDescription>Would you like to send a new invitation email to {user.email}?</DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="accent" onClick={onResendClick} isLoading={isLoading} disabled={isLoading}>
            Resend
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
